import React, { useState, useContext, useMemo } from "react";
import { ClientsContext, ClientGroupsContext, DivisionLensContext } from "@/App";
import { ChevronRight, BarChart3, Recycle } from "lucide-react";
import { divisionColor, divisionLabel } from "@/lib/divisions";
import DivisionGlyph from "@/components/icons/DivisionGlyph";

const fmtVal = (v) => {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${Math.round(v / 1_000)}K`;
  return `$${Math.round(v)}`;
};
const fmtINRk = (v) => {
  if (v >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(1)}Cr`;
  if (v >= 1_000) return `₹${Math.round(v / 1_000)}K`;
  return `₹${Math.round(v)}`;
};

export default function ClientOverviewSection({ clientStats = {}, activeValueByClient = {}, scrapSales = {}, showValue }) {
  const { clients } = useContext(ClientsContext);
  const { clientGroups } = useContext(ClientGroupsContext);
  // Division lens (nav polish, 14-Jul): scoped = render ONLY the scoped
  // division's tree with its own totals — never misleading $0 rows for the
  // rest. The header chip names the view and offers the way back.
  const { lens: divisionLens, setLens: setDivisionLens, lockedDivision } = useContext(DivisionLensContext) || { lens: "all" };
  const lensScoped = divisionLens !== "all";

  const [mode, setMode] = useState("total");
  const [expanded, setExpanded] = useState(new Set());

  const modeLabel = mode === "active" ? "active" : "total incl. shipped";

  // ── Data computation ────────────────────────────────────────────────────────

  const { groupRows, grandStats, ungroupedClients, ungroupedStats } = useMemo(() => {
    const agg = (clientNames) => {
      let lines = 0, value = 0;
      for (const name of clientNames) {
        const cs = clientStats[name];
        if (!cs) continue;
        lines += mode === "active" ? (cs.active_count || 0) : (cs.active_count || 0) + (cs.shipped_count || 0);
        value += mode === "active" ? (activeValueByClient[name] || 0) : (cs.total_value || 0);
      }
      return { lines, value };
    };

    // Lens filter works at the CLIENT level (a group shows if it holds any
    // scoped clients — even when the group itself carries no/another division
    // attribute), so mismatched clients can never silently vanish.
    const inLens = (c) => !lensScoped || (c.division || "metallic") === divisionLens;

    const topGroups = clientGroups.filter(g => g.type !== "subgroup" || !g.parent_id);
    const allGroupedIds = new Set(clientGroups.flatMap(g => g.client_ids || []));

    let rows = topGroups.map(group => {
      const subgroups = clientGroups.filter(sg => sg.type === "subgroup" && sg.parent_id === group.id);
      const directClientObjects = (group.client_ids || [])
        .map(cid => clients.find(c => c.id === cid))
        .filter(Boolean)
        .filter(inLens);
      const directClientNames = directClientObjects.map(c => c.name);

      const subgroupRows = subgroups.map(sg => {
        const sgClientObjects = (sg.client_ids || [])
          .map(cid => clients.find(c => c.id === cid))
          .filter(Boolean)
          .filter(inLens);
        const sgClientNames = sgClientObjects.map(c => c.name);
        return { ...sg, clientObjects: sgClientObjects, stats: agg(sgClientNames) };
      }).filter(sg => !lensScoped || sg.clientObjects.length > 0);

      const allGroupClientNames = [
        ...directClientNames,
        ...subgroupRows.flatMap(sg => sg.clientObjects.map(c => c.name)),
      ];

      return {
        ...group,
        subgroupRows,
        directClientObjects,
        stats: agg(allGroupClientNames),
        _lensClientCount: allGroupClientNames.length,
      };
    });
    if (lensScoped) rows = rows.filter(r => r._lensClientCount > 0);

    const allGroupedClientNames = rows.flatMap(row => [
      ...row.directClientObjects.map(c => c.name),
      ...row.subgroupRows.flatMap(sg => sg.clientObjects.map(c => c.name)),
    ]);
    // When scoped, totals come from the scoped tree only — never the
    // all-clients fallback (that would leak other divisions' numbers).
    const grandStats = agg(allGroupedClientNames.length > 0 ? allGroupedClientNames
                           : lensScoped ? [] : Object.keys(clientStats));
    const grandValue = grandStats.value;

    const rowsWithPct = rows.map(row => ({
      ...row,
      pct: grandValue > 0 ? row.stats.value / grandValue : 0,
    }));

    const ungroupedClients = clients
      .filter(c => !allGroupedIds.has(c.id))
      .filter(c => !lensScoped || (c.division || "metallic") === divisionLens);
    const ungroupedStats = agg(ungroupedClients.map(c => c.name));

    return { groupRows: rowsWithPct, grandStats, ungroupedClients, ungroupedStats };
  }, [clientGroups, clients, clientStats, activeValueByClient, mode, lensScoped, divisionLens]);

  const grandValue = grandStats.value;

  // Scrap sales — a distinct revenue stream folded into the "Total" headline only
  // (it's realized cash, not in-house WIP, so it never enters "Active"). Shown
  // only when the user can see values AND holds scrap-view permission. The client
  // donut/rows stay pure manufacturing — scrap sits beside them as by-product income.
  const showScrap = mode === "total" && showValue && scrapSales?.can_view && (scrapSales?.value_usd > 0);
  const combinedValue = grandValue + (showScrap ? scrapSales.value_usd : 0);

  const toggleExpanded = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const pctStr = (val) => grandValue > 0 ? `${Math.round((val / grandValue) * 100)}%` : "0%";

  // Sort a client array by value descending for the current mode
  const sortByValue = (clientArr) =>
    [...clientArr].sort((a, b) => {
      const va = mode === "active" ? (activeValueByClient[b.name] || 0) : (clientStats[b.name]?.total_value || 0);
      const vb = mode === "active" ? (activeValueByClient[a.name] || 0) : (clientStats[a.name]?.total_value || 0);
      return va - vb;
    });

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="rl-glass-edge bg-[hsl(var(--card))] rounded-lg p-4 h-full" data-testid="client-overview">

      {/* Section heading — matches TopBlockingReasonsChart header style */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-orange-500" />
          <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))]">Client Overview</h3>
          {lensScoped && (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full rl-glass-hairline"
              style={{ color: divisionColor(divisionLens), background: divisionColor(divisionLens) + "14" }}
              data-testid="client-overview-lens-chip"
            >
              <DivisionGlyph division={divisionLens} size={10} withTitle={false} />
              {divisionLabel(divisionLens)} view
              {!lockedDivision && (
                <button onClick={() => setDivisionLens("all")} className="underline underline-offset-2 opacity-80 hover:opacity-100">
                  show all
                </button>
              )}
            </span>
          )}
        </div>
        <div className="inline-flex bg-[hsl(var(--muted))]/60 rounded-full rl-carved p-[3px]">
          {["active", "total"].map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={
                mode === m
                  ? "px-3 py-1 text-xs font-semibold rounded-full rl-thumb bg-[hsl(var(--card))] text-[hsl(var(--foreground))] border border-[hsl(var(--border))] transition-all"
                  : "px-3 py-1 text-xs font-medium rounded-full text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-all"
              }
              data-testid={`client-overview-mode-${m}`}
            >
              {m.charAt(0).toUpperCase() + m.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Hero block */}
      <div className="mb-4">
        <div className="text-2xl font-semibold font-mono-data text-[hsl(var(--foreground))]">
          {showValue ? fmtVal(showScrap ? combinedValue : grandValue) : "—"}
          {showScrap && (
            <span className="ml-2 text-xs font-normal font-sans text-[hsl(var(--muted-foreground))]">total business sales</span>
          )}
        </div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
          {showScrap
            ? `${fmtVal(grandValue)} manufacturing (${modeLabel}) + ${fmtVal(scrapSales.value_usd)} scrap · FY26–27`
            : `${grandStats.lines} lines · ${modeLabel} · FY26–27`}
        </div>

        {/* Proportional bar */}
        {groupRows.length > 0 && grandValue > 0 && (
          <div className="flex gap-0.5 h-2 rounded-full rl-carved bg-[hsl(var(--muted))]/50 overflow-hidden mt-3">
            {groupRows.filter(g => g.stats.value > 0).map(group => (
              <div
                key={group.id}
                className="rounded-full rl-track-fill"
                style={{
                  flex: `${(group.stats.value / grandValue) * 100} 1 0%`,
                  minWidth: 0,
                  background: group.color,
                  opacity: 0.8,
                }}
              />
            ))}
          </div>
        )}

        {/* Legend */}
        {groupRows.length > 0 && (
          <div className="flex gap-4 mt-2 mb-1 flex-wrap">
            {groupRows.filter(g => g.stats.lines > 0 || g.stats.value > 0).map(group => (
              <div key={group.id} className="flex items-center gap-1.5">
                <div className="rounded-full shrink-0" style={{ width: "6px", height: "6px", background: group.color }} />
                <span className="text-xs font-medium text-[hsl(var(--foreground))]">{group.name}</span>
                <span className="text-xs font-mono-data text-[hsl(var(--muted-foreground))]">
                  {showValue ? pctStr(group.stats.value) : `${group.stats.lines}L`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Group rows */}
      <div>
        {groupRows.map((group) => {
          const isExp = expanded.has(group.id);
          const hasSubgroups = group.subgroupRows.length > 0;
          const hasDirectClients = group.directClientObjects.length > 0;
          const totalClientCount = group.directClientObjects.length + group.subgroupRows.flatMap(s => s.clientObjects).length;

          return (
            <div key={group.id} className="border-t border-[hsl(var(--border))]">
              {/* Group header row */}
              <div
                onClick={() => toggleExpanded(group.id)}
                className="py-3 cursor-pointer flex items-start"
                data-testid={`client-group-row-${group.id}`}
              >
                <ChevronRight
                  className={`w-3.5 h-3.5 text-[hsl(var(--muted-foreground))] transition-transform shrink-0 mt-0.5 mr-2 ${isExp ? "rotate-90" : ""}`}
                />
                {group.logo_svg ? (
                  <span
                    className="inline-flex items-center justify-center shrink-0 mt-0.5 mr-2.5 [&>svg]:w-full [&>svg]:h-full"
                    style={{ width: 16, height: 16 }}
                    dangerouslySetInnerHTML={{ __html: group.logo_svg }}
                  />
                ) : (
                  <div
                    className="rounded-full shrink-0 mt-1 mr-2.5"
                    style={{ width: "8px", height: "8px", background: group.color }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[hsl(var(--foreground))] flex items-center gap-2">
                    {group.name}
                    {group.division && (
                      <span
                        className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider"
                        style={{ color: divisionColor(group.division), background: divisionColor(group.division) + "1c" }}
                        title={`${divisionLabel(group.division)} division`}
                      >
                        division
                      </span>
                    )}
                  </div>
                  <div className="flex items-start gap-5 mt-2">
                    <StatPair label="Clients" value={totalClientCount} />
                    <StatPair label="Lines" value={group.stats.lines} />
                    <StatPair label="Value" value={showValue ? fmtVal(group.stats.value) : "—"} />
                    <StatPair label={lensScoped ? "% of view" : "% of total"} value={showValue ? pctStr(group.stats.value) : "—"} />
                  </div>
                </div>
              </div>

              {/* Expanded children */}
              {isExp && (
                <div className="pb-2 ml-6">
                  <div className="divide-y divide-[hsl(var(--border))]">
                    {/* Column headers */}
                    <div className="flex items-center justify-between pt-2 pb-1">
                      <span />
                      <div className="flex items-center">
                        <span className="w-14 text-right text-[9px] uppercase tracking-widest font-medium text-[hsl(var(--muted-foreground))]">Lines</span>
                        <span className="w-20 text-right text-[9px] uppercase tracking-widest font-medium text-[hsl(var(--muted-foreground))]">Value</span>
                      </div>
                    </div>

                    {/* Subgroups — each collapsed by default, expand on click */}
                    {hasSubgroups && group.subgroupRows.map(sg => {
                      const isSgExp = expanded.has(sg.id);
                      const sortedSgClients = sortByValue(sg.clientObjects);
                      return (
                        <div key={sg.id}>
                          <div
                            className="flex items-center justify-between py-2 border-l-2 pl-2 cursor-pointer"
                            style={{ borderLeftColor: sg.color }}
                            onClick={() => toggleExpanded(sg.id)}
                            data-testid={`client-subgroup-row-${sg.id}`}
                          >
                            <div className="flex items-center gap-1.5">
                              {sg.logo_svg && (
                                <span
                                  className="inline-flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full"
                                  style={{ width: 14, height: 14 }}
                                  dangerouslySetInnerHTML={{ __html: sg.logo_svg }}
                                />
                              )}
                              <ChevronRight
                                className={`w-3 h-3 transition-transform shrink-0 ${isSgExp ? "rotate-90" : ""}`}
                                style={{ color: sg.color }}
                              />
                              <span className="text-xs font-semibold text-[hsl(var(--foreground))]">{sg.name}</span>
                              <span className="text-[9px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border))] px-1.5 py-0.5 rounded">
                                group
                              </span>
                            </div>
                            <div className="flex items-center">
                              <span className="w-14 text-right text-xs font-semibold font-mono-data text-[hsl(var(--foreground))]">{sg.stats.lines}</span>
                              <span className="w-20 text-right text-xs font-semibold font-mono-data text-[hsl(var(--foreground))]">{showValue ? fmtVal(sg.stats.value) : "—"}</span>
                            </div>
                          </div>

                          {/* Subgroup clients — shown only when subgroup expanded */}
                          {isSgExp && (
                            <div className="divide-y divide-[hsl(var(--border))]/40 ml-3">
                              {sortedSgClients.map(client => (
                                <ClientFlexRow
                                  key={client.id}
                                  client={client}
                                  stats={clientStats[client.name]}
                                  mode={mode}
                                  activeVal={activeValueByClient[client.name]}
                                  showValue={showValue}
                                  logoSvg={sg.logo_svg || ""}
                                  variant="subgroup"
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* "Direct" divider label */}
                    {hasSubgroups && hasDirectClients && (
                      <div className="text-[10px] uppercase tracking-widest font-medium text-[hsl(var(--muted-foreground))] py-1.5">
                        Direct
                      </div>
                    )}

                    {/* Direct clients — sorted by value */}
                    {hasDirectClients && (
                      <div className="divide-y divide-[hsl(var(--border))]/40">
                        {sortByValue(group.directClientObjects).map(client => (
                          <ClientFlexRow
                            key={client.id}
                            client={client}
                            stats={clientStats[client.name]}
                            mode={mode}
                            activeVal={activeValueByClient[client.name]}
                            showValue={showValue}
                            logoSvg={client.logo_svg || group.logo_svg || ""}
                            variant="direct"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Ungrouped section */}
      <div className="border-t border-[hsl(var(--border))] py-3 opacity-40">
        <div className="flex items-center gap-2">
          <div className="flex gap-0.5">
            {[0, 1, 2].map(i => (
              <div key={i} className="w-1 h-1 rounded-full bg-[hsl(var(--muted-foreground))]" />
            ))}
          </div>
          <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-medium px-2 py-0.5 rounded-md rl-carved bg-[hsl(var(--muted))]/50">
            Ungrouped · {ungroupedClients.length} clients
          </span>
          {ungroupedStats.lines > 0 && (
            <span className="text-xs font-mono-data text-[hsl(var(--muted-foreground))]">
              {ungroupedStats.lines} lines{showValue ? ` · ${fmtVal(ungroupedStats.value)}` : ""}
            </span>
          )}
        </div>
        {ungroupedClients.length > 0 && (
          <div className="divide-y divide-[hsl(var(--border))]/40 pl-6 mt-1">
            {sortByValue(ungroupedClients).map(client => (
              <ClientFlexRow
                key={client.id}
                client={client}
                stats={clientStats[client.name]}
                mode={mode}
                activeVal={activeValueByClient[client.name]}
                showValue={showValue}
                logoSvg={client.logo_svg || ""}
                variant="direct"
              />
            ))}
          </div>
        )}
      </div>

      {/* Scrap sales — by-product income, kept distinct from the client mix above */}
      {showScrap && (
        <div
          className="flex items-center justify-between mt-1 px-3 py-2.5 rounded-lg border border-violet-500/25 bg-violet-500/[0.06]"
          title={`Scrap sold to ${scrapSales.dealer_count} dealer${scrapSales.dealer_count === 1 ? "" : "s"} this FY · ≈ ${fmtINRk(scrapSales.value_inr)} reference`}
          data-testid="client-overview-scrap"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-violet-700 dark:text-violet-300">
            <Recycle className="w-4 h-4" /> Scrap sales
            <span className="text-[10px] uppercase tracking-wider font-semibold text-violet-500/80">by-product</span>
          </span>
          <span className="text-sm font-mono-data text-violet-700 dark:text-violet-300">
            <span className="font-semibold">{fmtVal(scrapSales.value_usd)}</span>
            <span className="ml-1.5 text-xs opacity-80">· {scrapSales.count} sale{scrapSales.count === 1 ? "" : "s"}</span>
          </span>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatPair({ label, value }) {
  return (
    <div className="text-right min-w-[40px]">
      <div className="text-sm font-semibold font-mono-data text-[hsl(var(--foreground))]">{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] mt-0.5">{label}</div>
    </div>
  );
}

function ClientFlexRow({ client, stats, mode, activeVal, showValue, logoSvg, variant }) {
  if (!stats) return null;
  const lines = mode === "active"
    ? (stats.active_count || 0)
    : (stats.active_count || 0) + (stats.shipped_count || 0);
  const value = mode === "active" ? (activeVal || 0) : (stats.total_value || 0);
  const resolved = logoSvg ?? client.logo_svg ?? "";

  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2 min-w-0">
        {variant === "subgroup" ? (
          <div
            className="shrink-0 rounded-full"
            style={{ width: "2px", height: "14px", background: client.color }}
          />
        ) : resolved ? (
          <span
            className="inline-flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full"
            style={{ width: 14, height: 14 }}
            dangerouslySetInnerHTML={{ __html: resolved }}
          />
        ) : (
          <span
            className="inline-flex items-center justify-center shrink-0 rounded-full text-[6px] font-bold leading-none"
            style={{ width: 14, height: 14, color: client.color, backgroundColor: client.color + "20" }}
          >
            {client.name.split(" ").slice(0, 2).map(w => w[0]).join("")}
          </span>
        )}
        <span className="text-xs text-[hsl(var(--foreground))] truncate">{client.name}</span>
      </div>
      <div className="flex items-center shrink-0">
        <span className="w-14 text-right text-xs font-mono-data text-[hsl(var(--foreground))]">{lines}</span>
        <span className="w-20 text-right text-xs font-mono-data text-[hsl(var(--muted-foreground))]">
          {showValue ? fmtVal(value) : "—"}
        </span>
      </div>
    </div>
  );
}
